import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { financeiroApi, colaboradoresApi } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, DollarSign, TrendingUp, TrendingDown, ArrowUpCircle, ArrowDownCircle, Tags, CheckCircle2, Clock3, Copy, User } from 'lucide-react';

const emptyTransacao = {
  tipo: 'entrada',
  categoria: '',
  descricao: '',
  valor: 0,
  data: ''
};

const Financeiro = () => {
  const [transacoes, setTransacoes] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('mes');
  const [showForm, setShowForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTransacao, setSelectedTransacao] = useState(null);
  const [formData, setFormData] = useState({ ...emptyTransacao });
  const [saving, setSaving] = useState(false);

  // Categorias management
  const [showCategoriasDialog, setShowCategoriasDialog] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [categoriaForm, setCategoriaForm] = useState({ nome: '', tipo: 'entrada' });
  const [savingCategoria, setSavingCategoria] = useState(false);
  const [categoriaToDelete, setCategoriaToDelete] = useState(null);

  // Filters for pagamento de prestadores
  const [colaboradores, setColaboradores] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all'); // all | pago | pendente
  const [colaboradorFilter, setColaboradorFilter] = useState('all');
  const [tipoFilter, setTipoFilter] = useState('all');
  const [pagina, setPagina] = useState(1);
  const POR_PAGINA = 100;

  const loadCategorias = useCallback(async () => {
    try {
      const res = await financeiroApi.listCategorias();
      setCategorias(res.data);
    } catch (error) {
      console.error('Error loading categorias:', error);
    }
  }, []);

  const loadColaboradores = useCallback(async () => {
    try {
      const res = await colaboradoresApi.list();
      setColaboradores(res.data);
    } catch (error) {
      console.error('Error loading colaboradores:', error);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { periodo };
      if (statusFilter !== 'all') params.status_pagamento = statusFilter;
      if (colaboradorFilter !== 'all') params.colaborador_id = colaboradorFilter;
      if (tipoFilter !== 'all') params.tipo = tipoFilter;
      const [transacoesRes, resumoRes] = await Promise.all([
        financeiroApi.listTransacoes(params),
        financeiroApi.getResumo(periodo)
      ]);
      setTransacoes(transacoesRes.data);
      setResumo(resumoRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [periodo, statusFilter, colaboradorFilter, tipoFilter]);

  useEffect(() => {
    setPagina(1);
  }, [periodo, statusFilter, colaboradorFilter, tipoFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadColaboradores();
  }, [loadColaboradores]);

  useEffect(() => {
    loadCategorias();
  }, [loadCategorias]);

  const handleOpenForm = (transacao = null) => {
    if (transacao) {
      setSelectedTransacao(transacao);
      setFormData({
        tipo: transacao.tipo,
        categoria: transacao.categoria,
        descricao: transacao.descricao,
        valor: transacao.valor,
        data: transacao.data
      });
    } else {
      setSelectedTransacao(null);
      // Set default date to today (Brasília time)
      const today = new Date();
      const brasiliaDate = today.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
      setFormData({ 
        ...emptyTransacao,
        data: brasiliaDate
      });
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedTransacao(null);
    setFormData({ ...emptyTransacao });
  };

  const handleTipoChange = (tipo) => {
    setFormData({
      ...formData,
      tipo,
      categoria: '' // Reset category when type changes
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.categoria) {
      toast.error('Selecione uma categoria');
      return;
    }

    setSaving(true);

    try {
      if (selectedTransacao) {
        await financeiroApi.updateTransacao(selectedTransacao.id, formData);
        toast.success('Transação atualizada!');
      } else {
        await financeiroApi.createTransacao(formData);
        toast.success('Transação registrada!');
      }
      handleCloseForm();
      loadData();
    } catch (error) {
      console.error('Error saving transacao:', error);
      toast.error('Erro ao salvar transação');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (transacao) => {
    try {
      const res = await financeiroApi.toggleStatus(transacao.id);
      const newStatus = res.data.status_pagamento;
      toast.success(newStatus === 'pago' ? 'Marcado como pago' : 'Marcado como pendente');
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao alterar status');
    }
  };

  const handleCopyPix = async (chave) => {
    try {
      await navigator.clipboard.writeText(chave);
      toast.success('Chave PIX copiada!');
    } catch {
      toast.error('Erro ao copiar chave');
    }
  };

  const totaisPendentes = useMemo(() => {
    const pendentes = transacoes.filter((t) => t.status_pagamento === 'pendente');
    const totalPendente = pendentes.reduce((sum, t) => sum + t.valor, 0);
    return { count: pendentes.length, total: totalPendente };
  }, [transacoes]);

  const colaboradorMap = useMemo(() => {
    const m = {};
    colaboradores.forEach((c) => { m[c.id] = c; });
    return m;
  }, [colaboradores]);

  const handleDelete = async () => {
    if (!selectedTransacao) return;

    try {
      await financeiroApi.deleteTransacao(selectedTransacao.id);
      toast.success('Transação excluída!');
      setShowDeleteDialog(false);
      setSelectedTransacao(null);
      loadData();
    } catch (error) {
      console.error('Error deleting transacao:', error);
      toast.error('Erro ao excluir transação');
    }
  };

  const handleSaveCategoria = async (e) => {
    e.preventDefault();
    if (!categoriaForm.nome.trim()) {
      toast.error('Informe o nome da categoria');
      return;
    }
    setSavingCategoria(true);
    try {
      if (editingCategoria) {
        await financeiroApi.updateCategoria(editingCategoria.id, categoriaForm);
        toast.success('Categoria atualizada!');
      } else {
        await financeiroApi.createCategoria(categoriaForm);
        toast.success('Categoria criada!');
      }
      setEditingCategoria(null);
      setCategoriaForm({ nome: '', tipo: 'entrada' });
      loadCategorias();
    } catch (error) {
      console.error('Error saving categoria:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar categoria');
    } finally {
      setSavingCategoria(false);
    }
  };

  const handleEditCategoria = (cat) => {
    setEditingCategoria(cat);
    setCategoriaForm({ nome: cat.nome, tipo: cat.tipo });
  };

  const handleCancelEditCategoria = () => {
    setEditingCategoria(null);
    setCategoriaForm({ nome: '', tipo: 'entrada' });
  };

  const handleDeleteCategoria = async () => {
    if (!categoriaToDelete) return;
    try {
      await financeiroApi.deleteCategoria(categoriaToDelete.id);
      toast.success('Categoria excluída!');
      setCategoriaToDelete(null);
      loadCategorias();
    } catch (error) {
      console.error('Error deleting categoria:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir categoria');
      setCategoriaToDelete(null);
    }
  };

  const categoriasDoTipo = categorias.filter((c) => c.tipo === formData.tipo);

  return (
    <div className="space-y-6" data-testid="financeiro-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Work Sans' }}>
            Financeiro
          </h1>
          <p className="text-gray-500 mt-1">
            Controle de fluxo de caixa
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setShowCategoriasDialog(true)} data-testid="manage-categorias-btn">
            <Tags size={18} className="mr-2" />
            Gerenciar Categorias
          </Button>
          <Button onClick={() => handleOpenForm()} data-testid="add-transacao-btn">
            <Plus size={18} className="mr-2" />
            Nova Transação
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="stat-card border-l-4 border-l-green-500" data-testid="card-entradas">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 uppercase tracking-wide">Total Entradas</p>
                <p className="text-2xl font-bold text-green-600 mt-1" style={{ fontFamily: 'Work Sans' }}>
                  {loading ? '-' : formatCurrency(resumo?.total_entradas || 0)}
                </p>
              </div>
              <ArrowUpCircle className="w-10 h-10 text-green-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card border-l-4 border-l-red-500" data-testid="card-saidas">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 uppercase tracking-wide">Total Saídas</p>
                <p className="text-2xl font-bold text-red-600 mt-1" style={{ fontFamily: 'Work Sans' }}>
                  {loading ? '-' : formatCurrency(resumo?.total_saidas || 0)}
                </p>
              </div>
              <ArrowDownCircle className="w-10 h-10 text-red-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card border-l-4 border-l-blue-500" data-testid="card-saldo">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 uppercase tracking-wide">Saldo</p>
                <p className={`text-2xl font-bold mt-1 ${(resumo?.saldo || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`} style={{ fontFamily: 'Work Sans' }}>
                  {loading ? '-' : formatCurrency(resumo?.saldo || 0)}
                </p>
              </div>
              <DollarSign className="w-10 h-10 text-blue-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card border-l-4 border-l-amber-500" data-testid="card-a-receber-parceiros">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 uppercase tracking-wide">A Receber (Parceiros)</p>
                <p className="text-2xl font-bold text-amber-600 mt-1" style={{ fontFamily: 'Work Sans' }}>
                  {loading ? '-' : formatCurrency(resumo?.a_receber_parceiros || 0)}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {loading ? '' : `${resumo?.qtd_faturas_parceiros_abertas || 0} fatura${(resumo?.qtd_faturas_parceiros_abertas || 0) !== 1 ? 's' : ''} em aberto`}
                </p>
              </div>
              <Clock3 className="w-10 h-10 text-amber-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <CardTitle style={{ fontFamily: 'Work Sans' }} className="flex items-center gap-2">
              <DollarSign size={20} />
              Transações
              {statusFilter === 'pendente' && (
                <Badge variant="outline" className="ml-2 border-amber-400 text-amber-700 bg-amber-50">
                  {totaisPendentes.count} pendente(s) — {formatCurrency(totaisPendentes.total)}
                </Badge>
              )}
            </CardTitle>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Select value={periodo} onValueChange={setPeriodo}>
                <SelectTrigger data-testid="filter-periodo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dia">Hoje</SelectItem>
                  <SelectItem value="semana">Semana</SelectItem>
                  <SelectItem value="mes">Mês</SelectItem>
                  <SelectItem value="ano">Ano</SelectItem>
                  <SelectItem value="all">Todas</SelectItem>
                </SelectContent>
              </Select>
              <Select value={tipoFilter} onValueChange={setTipoFilter}>
                <SelectTrigger data-testid="filter-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  <SelectItem value="entrada">Entradas</SelectItem>
                  <SelectItem value="saida">Saídas</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="pago">Pagos</SelectItem>
                  <SelectItem value="pendente">Pendentes</SelectItem>
                </SelectContent>
              </Select>
              <Select value={colaboradorFilter} onValueChange={setColaboradorFilter}>
                <SelectTrigger data-testid="filter-colaborador"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos colaboradores</SelectItem>
                  {colaboradores.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : transacoes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Nenhuma transação encontrada
            </div>
          ) : (
            <div className="table-fit">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th className="hidden md:table-cell">Tipo</th>
                    <th className="hidden xl:table-cell">Categoria</th>
                    <th>Descrição</th>
                    <th className="hidden lg:table-cell">Colaborador / PIX</th>
                    <th>Valor</th>
                    <th>Status</th>
                    <th className="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {transacoes.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA).map((transacao) => {
                    const colab = transacao.colaborador_id ? colaboradorMap[transacao.colaborador_id] : null;
                    const isPago = transacao.status_pagamento === 'pago';
                    return (
                      <tr key={transacao.id} data-testid={`transacao-row-${transacao.id}`}>
                        <td className="whitespace-nowrap">{formatDate(transacao.data)}</td>
                        <td className="hidden md:table-cell">
                          <Badge variant={transacao.tipo === 'entrada' ? 'default' : 'destructive'}>
                            {transacao.tipo === 'entrada' ? (
                              <TrendingUp size={14} className="mr-1" />
                            ) : (
                              <TrendingDown size={14} className="mr-1" />
                            )}
                            {transacao.tipo === 'entrada' ? 'Entrada' : 'Saída'}
                          </Badge>
                        </td>
                        <td className="hidden xl:table-cell">{transacao.categoria}</td>
                        <td className="cell-ellipsis" title={transacao.descricao}>{transacao.descricao}</td>
                        <td className="hidden lg:table-cell">
                          {transacao.colaborador_nome ? (
                            <div className="flex items-center gap-1.5 text-sm">
                              <User size={13} className="text-gray-400" />
                              <span>{transacao.colaborador_nome}</span>
                              {colab?.pix_chave && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-1.5"
                                  onClick={() => handleCopyPix(colab.pix_chave)}
                                  title={`PIX (${colab.pix_tipo}): ${colab.pix_chave}`}
                                  data-testid={`copy-pix-${transacao.id}`}
                                >
                                  <Copy size={12} className="text-emerald-600" />
                                </Button>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                        <td className={transacao.tipo === 'entrada' ? 'text-green-600 font-medium tabular-nums' : 'text-red-600 font-medium tabular-nums'}>
                          {transacao.tipo === 'entrada' ? '+' : '-'}{formatCurrency(transacao.valor)}
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => handleToggleStatus(transacao)}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border transition ${
                              isPago
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                            }`}
                            title="Alternar status"
                            data-testid={`toggle-status-${transacao.id}`}
                          >
                            {isPago ? <CheckCircle2 size={12} /> : <Clock3 size={12} />}
                            {isPago ? 'Pago' : 'Pendente'}
                          </button>
                        </td>
                        <td>
                          <div className="flex items-center gap-2 flex-nowrap justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenForm(transacao)}
                              data-testid={`edit-transacao-${transacao.id}`}
                            >
                              <Pencil size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedTransacao(transacao);
                                setShowDeleteDialog(true);
                              }}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-transacao-${transacao.id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {transacoes.length > POR_PAGINA && (
                <div className="flex items-center justify-between px-2 py-3 border-t" data-testid="paginacao-transacoes">
                  <span className="text-sm text-gray-500">
                    Mostrando {(pagina - 1) * POR_PAGINA + 1}–{Math.min(pagina * POR_PAGINA, transacoes.length)} de {transacoes.length} transações
                  </span>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={pagina === 1} onClick={() => setPagina((p) => p - 1)} data-testid="pagina-anterior-btn">
                      Anterior
                    </Button>
                    <span className="text-sm text-gray-600">{pagina} / {Math.ceil(transacoes.length / POR_PAGINA)}</span>
                    <Button variant="outline" size="sm" disabled={pagina >= Math.ceil(transacoes.length / POR_PAGINA)} onClick={() => setPagina((p) => p + 1)} data-testid="proxima-pagina-btn">
                      Próxima
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Categories Summary */}
      {resumo?.por_categoria && Object.keys(resumo.por_categoria).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Work Sans' }}>Resumo por Categoria</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(resumo.por_categoria).map(([categoria, valores]) => (
                <div key={categoria} className="p-4 bg-gray-50 rounded-lg border">
                  <p className="font-medium mb-2">{categoria}</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600">+{formatCurrency(valores.entrada)}</span>
                    <span className="text-red-600">-{formatCurrency(valores.saida)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Work Sans' }}>
              {selectedTransacao ? 'Editar Transação' : 'Nova Transação'}
            </DialogTitle>
            <DialogDescription>
              Registre uma entrada ou saída
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo *</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={formData.tipo === 'entrada' ? 'default' : 'outline'}
                  className={`flex-1 ${formData.tipo === 'entrada' ? 'bg-green-600 hover:bg-green-700 text-white' : ''}`}
                  onClick={() => handleTipoChange('entrada')}
                  data-testid="tipo-entrada-btn"
                >
                  <TrendingUp size={18} className="mr-2" />
                  Entrada
                </Button>
                <Button
                  type="button"
                  variant={formData.tipo === 'saida' ? 'default' : 'outline'}
                  className={`flex-1 ${formData.tipo === 'saida' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                  onClick={() => handleTipoChange('saida')}
                  data-testid="tipo-saida-btn"
                >
                  <TrendingDown size={18} className="mr-2" />
                  Saída
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="categoria">Categoria *</Label>
              <Select 
                value={formData.categoria} 
                onValueChange={(value) => setFormData({ ...formData, categoria: value })}
              >
                <SelectTrigger data-testid="categoria-select">
                  <SelectValue placeholder="Selecione a categoria" />
                </SelectTrigger>
                <SelectContent>
                  {categoriasDoTipo.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      Nenhuma categoria. Cadastre em &quot;Gerenciar Categorias&quot;.
                    </div>
                  ) : (
                    categoriasDoTipo.map((cat) => (
                      <SelectItem key={cat.id} value={cat.nome}>
                        {cat.nome}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição *</Label>
              <Textarea
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Descreva a transação..."
                required
                rows={2}
                data-testid="descricao-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="valor">Valor (R$) *</Label>
                <Input
                  id="valor"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.valor}
                  onChange={(e) => setFormData({ ...formData, valor: parseFloat(e.target.value) || 0 })}
                  required
                  data-testid="valor-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="data">Data *</Label>
                <Input
                  id="data"
                  type="date"
                  value={formData.data}
                  onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                  required
                  data-testid="data-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-transacao-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta transação? 
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Categorias Management Dialog */}
      <Dialog open={showCategoriasDialog} onOpenChange={(open) => {
        setShowCategoriasDialog(open);
        if (!open) handleCancelEditCategoria();
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="categorias-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Work Sans' }} className="flex items-center gap-2">
              <Tags size={20} />
              Gerenciar Categorias
            </DialogTitle>
            <DialogDescription>
              Crie, edite e exclua categorias de entrada e saída.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveCategoria} className="space-y-3 p-4 bg-gray-50 rounded-lg border">
            <p className="font-medium text-sm" style={{ fontFamily: 'Work Sans' }}>
              {editingCategoria ? 'Editar Categoria' : 'Nova Categoria'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="cat-nome">Nome *</Label>
                <Input
                  id="cat-nome"
                  value={categoriaForm.nome}
                  onChange={(e) => setCategoriaForm({ ...categoriaForm, nome: e.target.value })}
                  placeholder="Ex: Aluguel"
                  required
                  data-testid="categoria-nome-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="cat-tipo">Tipo *</Label>
                <Select
                  value={categoriaForm.tipo}
                  onValueChange={(value) => setCategoriaForm({ ...categoriaForm, tipo: value })}
                >
                  <SelectTrigger data-testid="categoria-tipo-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="entrada">Entrada</SelectItem>
                    <SelectItem value="saida">Saída</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              {editingCategoria && (
                <Button type="button" variant="outline" onClick={handleCancelEditCategoria}>
                  Cancelar
                </Button>
              )}
              <Button type="submit" disabled={savingCategoria} data-testid="save-categoria-btn">
                {savingCategoria ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando...</>
                ) : (
                  editingCategoria ? 'Atualizar' : 'Adicionar'
                )}
              </Button>
            </div>
          </form>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <h4 className="font-medium text-sm mb-2 text-green-700 flex items-center gap-1">
                <TrendingUp size={16} /> Entradas
              </h4>
              <div className="space-y-2">
                {categorias.filter((c) => c.tipo === 'entrada').length === 0 ? (
                  <p className="text-sm text-gray-500">Nenhuma categoria.</p>
                ) : (
                  categorias.filter((c) => c.tipo === 'entrada').map((cat) => (
                    <div key={cat.id} className="flex items-center justify-between p-2 border rounded-md bg-white" data-testid={`categoria-row-${cat.id}`}>
                      <span className="text-sm">{cat.nome}</span>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEditCategoria(cat)} data-testid={`edit-categoria-${cat.id}`}>
                          <Pencil size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setCategoriaToDelete(cat)} data-testid={`delete-categoria-${cat.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div>
              <h4 className="font-medium text-sm mb-2 text-red-700 flex items-center gap-1">
                <TrendingDown size={16} /> Saídas
              </h4>
              <div className="space-y-2">
                {categorias.filter((c) => c.tipo === 'saida').length === 0 ? (
                  <p className="text-sm text-gray-500">Nenhuma categoria.</p>
                ) : (
                  categorias.filter((c) => c.tipo === 'saida').map((cat) => (
                    <div key={cat.id} className="flex items-center justify-between p-2 border rounded-md bg-white" data-testid={`categoria-row-${cat.id}`}>
                      <span className="text-sm">{cat.nome}</span>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEditCategoria(cat)} data-testid={`edit-categoria-${cat.id}`}>
                          <Pencil size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setCategoriaToDelete(cat)} data-testid={`delete-categoria-${cat.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoriasDialog(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Categoria Confirmation */}
      <AlertDialog open={!!categoriaToDelete} onOpenChange={(open) => !open && setCategoriaToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Categoria</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a categoria &quot;{categoriaToDelete?.nome}&quot;?
              Categorias em uso por transações não podem ser excluídas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCategoria} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-categoria-btn">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Financeiro;
